import { rpcClient } from 'src/api/rpc/client';
import { useMutation } from '@tanstack/react-query';
import { useToast } from 'src/context/ToastContext';
import type { ExportInboundEmailForDebugOutput } from '@erp/rpc';
import { packExportPayloadAsZip } from './inboundEmailDebugZip';

/** Trigger a browser download of a Blob with the given file name. */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function buildExportFileName(
  payload: ExportInboundEmailForDebugOutput,
): string {
  const id = payload.inboundEmail.id;
  const ts = new Date(payload.exportedAt).toISOString().replace(/[:.]/g, '-');
  return `inbound-email-${id}-${ts}.zip`;
}

/**
 * Admin-only debug export. Calls the `exportForDebug` RPC and bundles
 * the manifest + original attachment binaries into a single .zip file
 * (see `inboundEmailDebugZip.ts` for the layout) so the developer can
 * hand the dump to a local backend, reimport, and reprocess from
 * `splitDocumentsHandler`.
 */
export function useExportInboundEmailForDebug() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (inboundEmailId: string) => {
      const payload =
        await rpcClient.communications.inboundEmails.exportForDebug({
          inboundEmailId,
        });
      const blob = await packExportPayloadAsZip(payload);
      return { payload, blob };
    },
    onSuccess: ({ payload, blob }) => {
      downloadBlob(blob, buildExportFileName(payload));
      const attachmentCount = payload.originalAttachments.length;
      const sizeMiB = (blob.size / (1024 * 1024)).toFixed(2);
      showToast({
        title: 'Email Exported',
        message: `Downloaded ZIP with ${attachmentCount} attachment(s) (${sizeMiB} MiB)`,
        color: 'success',
        icon: 'check_circle',
      });
    },
    onError: (error) => {
      console.error('Failed to export inbound email for debug:', error);
      showToast({
        title: 'Export Failed',
        message:
          error instanceof Error ? error.message : 'Failed to export email',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
